"use client";

import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMarkupStore } from "../store";
import type { MarkupConfig } from "../types";
import { matchesShortcut } from "../utils/shortcuts";
import { isDeviceFrame } from "../utils/deviceFrame";
import { readPendingFocus } from "../utils/focusNavigation";
import { readTicketLink } from "../utils/shareLink";
import { initFirebase, setFirebaseEmulators } from "../config/firebaseConfig";
import { setDebug } from "../utils/logger";
import { isProductionSite } from "../utils/branch";

export interface MarkupWidgetProps {
    config?: Partial<MarkupConfig>;
}

// Only this file is loaded up front. Everything else — the panel, pins,
// Firebase — arrives with this chunk, on first use.
const MarkupWidgetApp = React.lazy(() => import("./MarkupWidgetApp"));

const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Whether anything on this page load needs the full widget straight away.
 * An ordinary visitor to a client's site matches none of these, and never
 * downloads the rest of the widget or the Firebase SDK.
 */
function needsWidgetNow(config?: Partial<MarkupConfig>): boolean {
    const { isVisible, isAuthenticated } = useMarkupStore.getState();
    return (
        isVisible ||
        isAuthenticated || // a reviewer's session: pins should show
        !!config?.defaultOpen ||
        !!readPendingFocus() || // arriving from "open ticket on another page"
        !!readTicketLink() // arriving from a ticket link
    );
}

/**
 * The public widget component: a small client-only shell.
 *
 * - Renders nothing on the server and on the first client render, so it can
 *   never cause a hydration mismatch (the widget is driven entirely by browser
 *   state — localStorage, auth, the live DOM).
 * - Renders nothing inside the responsive preview's iframe, where the host page
 *   loads it a second time.
 * - Renders nothing, loads nothing and listens for nothing on a production
 *   site: a main/master build, or a domain that isn't recognisably dev.
 * - Listens for the show/hide shortcut and the script-tag API's open/close
 *   events, and only then loads the widget proper.
 */
export const MarkupWidget: React.FC<MarkupWidgetProps> = (props) => {
    const { config } = props;
    const [mode, setMode] = useState<
        "pending" | "idle" | "active" | "preview" | "off"
    >("pending");
    const off = isProductionSite(config);

    // Hand over the Firebase project and debug flag before anything loads.
    // Neither touches the Firebase SDK.
    const firebaseConfig = config?.firebaseConfig;
    const appliedConfig = useRef<unknown>(null);
    if (!off && firebaseConfig && appliedConfig.current !== firebaseConfig) {
        appliedConfig.current = firebaseConfig;
        initFirebase(firebaseConfig);
    }
    setDebug(config?.debug);
    if (config?.firebaseEmulators) setFirebaseEmulators(config.firebaseEmulators);

    // Layout effect: the state update re-renders synchronously, before the
    // browser paints.
    useIsomorphicLayoutEffect(() => {
        if (off) setMode("off");
        else if (isDeviceFrame()) setMode("preview");
        else setMode(needsWidgetNow(config) ? "active" : "idle");
        // Decided once per page load.
    }, []);

    // Show/hide shortcut, and the CDN build's MarkupWidget.open()/close().
    const shortcut = config?.shortcut || "ctrl+shift+m";
    useEffect(() => {
        if (mode !== "idle" && mode !== "active") return;
        const store = useMarkupStore.getState;

        const onKey = (e: KeyboardEvent) => {
            if (!matchesShortcut(e, shortcut)) return;
            e.preventDefault();
            store().setIsVisible(!store().isVisible);
            setMode("active");
        };
        const onOpen = () => {
            store().setIsVisible(true);
            store().setIsOpen(true);
            setMode("active");
        };
        const onClose = () => store().setIsOpen(false);

        document.addEventListener("keydown", onKey);
        window.addEventListener("markup-widget-open", onOpen);
        window.addEventListener("markup-widget-close", onClose);
        return () => {
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("markup-widget-open", onOpen);
            window.removeEventListener("markup-widget-close", onClose);
        };
    }, [mode, shortcut]);

    if (mode !== "active") return null;

    return (
        <Suspense fallback={null}>
            <MarkupWidgetApp {...props} />
        </Suspense>
    );
};

export default MarkupWidget;
